import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModuleContainer from "./ModuleContainer";
import { supabase } from "../../lib/supabase";
import { colors } from "../../lib/designTokens";
import { formatLocalIntentLabel } from "../../lib/localIntentLabels";
import "../opportunities/TorenControls.css";

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

type Props = {
  account?: string | null;
  customerBrand?: string;
  runId?: string | null;
  mode?: "demo" | "competitive" | "local" | "retail";
};

type ConsensusCategoryRow = {
  category: string;
  comparablePrompts: number;
  fullConsensusPrompts: number;
  majorityAlignedPrompts: number;
  splitSignalPrompts: number;
  fullConsensusRate: number;
  majorityAlignmentRate: number;
};

type PromptSignal = {
  provider: string;
  label: string;
  signal: string;
};

type PromptAgreementStatus = "clear" | "unclear";
type PromptFilter = "all" | PromptAgreementStatus;

type ConsensusPrompt = {
  id: string;
  category: string;
  promptText: string;
  agreementRate: number;
  agreeingModels: number;
  modelCount: number;
  status: PromptAgreementStatus;
  signals: PromptSignal[];
};

type PromptScore = {
  category: string;
  fullConsensus: boolean;
  majorityAligned: boolean;
  splitSignal: boolean;
  topSignalCount: number;
  modelCount: number;
  agreementRate: number;
};

const PROVIDER_ORDER = ["openai", "anthropic", "gemini", "perplexity"];

const MODEL_LABELS: Record<string, string> = {
  openai: "ChatGPT",
  anthropic: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity"
};

const SIGNALS_TO_IGNORE = new Set([
  "",
  "error",
  "failed",
  "timeout",
  "unknown",
  "undefined"
]);

function getProviderSortIndex(provider: string) {
  const index = PROVIDER_ORDER.indexOf(provider);
  return index === -1 ? 999 : index;
}

function formatCategory(value?: string | null, mode?: Props["mode"]) {
  if (mode === "local") return formatLocalIntentLabel(value);
  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}

function getValidProviderSignals(modelOutputs: any) {
  if (!modelOutputs || typeof modelOutputs !== "object") {
    return [];
  }

  return Object.entries(modelOutputs)
    .map(([provider, output]: [string, any]) => {
      const signal = String(output?.signal || "").trim().toLowerCase();

      if (SIGNALS_TO_IGNORE.has(signal)) {
        return null;
      }

      return {
        provider,
        signal
      };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => getProviderSortIndex(a.provider) - getProviderSortIndex(b.provider)) as Array<{
    provider: string;
    signal: string;
  }>;
}

function scoreSignals(
  category: string,
  providerSignals: Array<{ provider: string; signal: string }>
): PromptScore | null {
  if (providerSignals.length < 2) {
    return null;
  }

  const counts = providerSignals.reduce<Record<string, number>>((acc, item) => {
    acc[item.signal] = (acc[item.signal] || 0) + 1;
    return acc;
  }, {});

  const topSignalCount = Math.max(...Object.values(counts));
  const modelCount = providerSignals.length;
  const majorityThreshold = Math.floor(modelCount / 2) + 1;

  return {
    category,
    fullConsensus: topSignalCount === modelCount,
    majorityAligned: topSignalCount >= majorityThreshold,
    splitSignal: topSignalCount < majorityThreshold,
    topSignalCount,
    modelCount,
    agreementRate: topSignalCount / modelCount
  };
}

export function getNarrative({
  mode,
  highest,
  lowest,
  majorityAlignmentRate,
  fullConsensusRate,
  comparablePrompts
}: {
  mode: Props["mode"];
  highest: ConsensusCategoryRow;
  lowest: ConsensusCategoryRow;
  majorityAlignmentRate: number;
  fullConsensusRate: number;
  comparablePrompts: number;
}) {
  if (mode !== "local") {
    const context = "market pattern";
    return `Overall consensus is strong: most models agreed on ${formatPercent(
      majorityAlignmentRate
    )} of prompts. Exact agreement was ${formatPercent(
      fullConsensusRate
    )}, which means models usually saw the same ${context} even when they differed on signal strength. ${formatCategory(
      highest.category
    )} was the most consistent category, while ${formatCategory(
      lowest.category
    )} showed the most model variation.`;
  }

  const context = mode === "local" ? "Local response signals" : "response signals";

  return `This is an agreement measure, not a performance or recommendation-strength score. Across ${comparablePrompts} prompts, most models aligned on ${formatPercent(
    majorityAlignmentRate
  )}. Exact agreement was ${formatPercent(
    fullConsensusRate
  )}. These rates describe how consistently models classified ${context}; agreement can be favorable or unfavorable. ${formatCategory(
    highest.category, mode
  )} was the most consistent category, while ${formatCategory(
    lowest.category, mode
  )} showed the most model variation.`;
}

function getSubtitle(mode: Props["mode"]) {
  if (mode === "local") {
    return "Compares monitored model response signals across the same Local buyer-intent prompts. Agreement does not indicate a favorable recommendation.";
  }

  if (mode === "retail") {
    return "Compares how monitored AI models classify your retail visibility across the same retail discovery and shopping-intent prompts.";
  }

  return "Compares how monitored AI models classify your brand visibility across the same buyer-journey prompts.";
}

export default function AIConsensus({
  account,
  runId,
  mode = "competitive"
}: Props) {
  const [rows, setRows] = useState<ConsensusCategoryRow[]>([]);
  const [modelLabels, setModelLabels] = useState<string[]>([]);
  const [status, setStatus] = useState<ModuleStatus>("loading");
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [consensusPrompts, setConsensusPrompts] = useState<ConsensusPrompt[]>([]);
  const [showPromptMix, setShowPromptMix] = useState(false);
  const [promptFilter, setPromptFilter] = useState<PromptFilter>("all");
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);

  const fetchConsensus = useCallback(async (isCurrent: () => boolean = () => true) => {
    if (!account || !runId) return;

    setRows([]);
    setLoadedScope(null);
    setModelLabels([]);
    setConsensusPrompts([]);
    setShowPromptMix(false);
    setPromptFilter("all");
    setExpandedPromptId(null);
    setStatus("loading");
    setStatusMessage("");

    const { data, error } = await supabase
      .from("prompt_results")
      .select("category, prompt_id, model_outputs")
      .eq("account_id", account)
      .eq("run_id", runId)
      .eq("status", "completed")
      .neq("visibility_signal", "error")
      .not("model_outputs", "is", null)
      .limit(1000);

    if (!isCurrent()) return;

    if (error) {
      console.error("[AIConsensus] Query error", error);
      setRows([]);
      setModelLabels([]);
      setConsensusPrompts([]);
      setStatus("error");
      setStatusMessage("AI consensus could not load right now.");
      return;
    }

    if (!data?.length) {
      setRows([]);
      setModelLabels([]);
      setConsensusPrompts([]);
      setStatus("empty");
      setStatusMessage("No model comparison data is available yet.");
      return;
    }

    const providerSet = new Set<string>();
    const scoredPrompts: PromptScore[] = [];
    const promptDrafts: Array<
      Omit<ConsensusPrompt, "id" | "promptText"> & { promptId: string }
    > = [];

    for (const row of data as any[]) {
      const providerSignals = getValidProviderSignals(row.model_outputs);

      providerSignals.forEach((item) => providerSet.add(item.provider));

      const score = scoreSignals(row.category || "unknown", providerSignals);

      if (score) {
        scoredPrompts.push(score);

        if (row.prompt_id) {
          promptDrafts.push({
            promptId: String(row.prompt_id),
            category: row.category || "unknown",
            agreementRate: score.agreementRate,
            agreeingModels: score.topSignalCount,
            modelCount: score.modelCount,
            status: score.splitSignal ? "unclear" : "clear",
            signals: providerSignals.map((item) => ({
              provider: item.provider,
              label: MODEL_LABELS[item.provider] || formatCategory(item.provider),
              signal: item.signal
            }))
          });
        }
      }
    }

    if (promptDrafts.length > 0) {
      const promptIds = Array.from(
        new Set(promptDrafts.map((item) => item.promptId))
      );

      const { data: promptRows, error: promptError } = await supabase
        .from("prompts")
        .select("prompt_id, prompt_text")
        .in("prompt_id", promptIds);

      if (!isCurrent()) return;

      if (promptError) {
        console.warn("[AIConsensus] Prompt text lookup error", promptError);
      }

      const promptTextById = new Map<string, string>(
        (promptRows || []).map((prompt: any) => [
          String(prompt.prompt_id),
          String(prompt.prompt_text || "")
        ])
      );

      setConsensusPrompts(
        promptDrafts
          .map(({ promptId, ...item }) => ({
            ...item,
            id: promptId,
            promptText:
              promptTextById.get(promptId) || "Prompt text unavailable."
          }))
          .sort((a, b) => {
            const categoryComparison = formatCategory(a.category).localeCompare(
              formatCategory(b.category)
            );

            if (categoryComparison !== 0) return categoryComparison;
            if (a.agreementRate !== b.agreementRate) {
              return a.agreementRate - b.agreementRate;
            }

            return a.promptText.localeCompare(b.promptText);
          })
      );
    } else {
      setConsensusPrompts([]);
    }

    const detectedModelLabels = Array.from(providerSet)
      .sort((a, b) => {
        const aIndex = getProviderSortIndex(a);
        const bIndex = getProviderSortIndex(b);

        if (aIndex === bIndex) return a.localeCompare(b);

        return aIndex - bIndex;
      })
      .map((provider) => MODEL_LABELS[provider] || formatCategory(provider));

    setModelLabels(detectedModelLabels);

    if (providerSet.size < 2) {
      setRows([]);
      setStatus("unavailable");
      setStatusMessage(
        `Consensus requires at least two monitored AI models. Current coverage: ${
          detectedModelLabels.join(", ") || "unavailable"
        }.`
      );
      return;
    }

    if (!scoredPrompts.length) {
      setRows([]);
      setStatus("empty");
      setStatusMessage("Not enough comparable model output is available yet.");
      return;
    }

    const categoryMap = new Map<
      string,
      {
        comparablePrompts: number;
        fullConsensusPrompts: number;
        majorityAlignedPrompts: number;
        splitSignalPrompts: number;
      }
    >();

    for (const prompt of scoredPrompts) {
      const current =
        categoryMap.get(prompt.category) ||
        {
          comparablePrompts: 0,
          fullConsensusPrompts: 0,
          majorityAlignedPrompts: 0,
          splitSignalPrompts: 0
        };

      current.comparablePrompts += 1;

      if (prompt.fullConsensus) {
        current.fullConsensusPrompts += 1;
      }

      if (prompt.majorityAligned) {
        current.majorityAlignedPrompts += 1;
      }

      if (prompt.splitSignal) {
        current.splitSignalPrompts += 1;
      }

      categoryMap.set(prompt.category, current);
    }

    const consensusRows = Array.from(categoryMap.entries())
      .map(([category, value]) => ({
        category,
        comparablePrompts: value.comparablePrompts,
        fullConsensusPrompts: value.fullConsensusPrompts,
        majorityAlignedPrompts: value.majorityAlignedPrompts,
        splitSignalPrompts: value.splitSignalPrompts,
        fullConsensusRate:
          value.comparablePrompts > 0
            ? value.fullConsensusPrompts / value.comparablePrompts
            : 0,
        majorityAlignmentRate:
          value.comparablePrompts > 0
            ? value.majorityAlignedPrompts / value.comparablePrompts
            : 0
      }))
      .sort((a, b) => {
        if (a.majorityAlignmentRate !== b.majorityAlignmentRate) {
          return a.majorityAlignmentRate - b.majorityAlignmentRate;
        }

        return a.fullConsensusRate - b.fullConsensusRate;
      });

    if (!consensusRows.length) {
      setRows([]);
      setStatus("empty");
      setStatusMessage("Not enough comparable model output is available yet.");
      return;
    }

    setRows(consensusRows);
    setLoadedScope(`${account}:${runId}`);
    setStatus("ready");
  }, [account, runId]);

  useEffect(() => {
    let cancelled = false;
    if (!account || !runId) {
      setRows([]);
      setLoadedScope(null);
      setModelLabels([]);
      setConsensusPrompts([]);
      setStatus("unavailable");
      setStatusMessage("AI consensus is not available for this run yet.");
      return () => { cancelled = true; };
    }

    fetchConsensus(() => !cancelled);
    return () => { cancelled = true; };
  }, [account, runId, mode, fetchConsensus]);

  const summary = useMemo(() => {
    if (!rows.length) return null;

    const comparablePrompts = rows.reduce(
      (sum, row) => sum + row.comparablePrompts,
      0
    );
    const fullConsensusPrompts = rows.reduce(
      (sum, row) => sum + row.fullConsensusPrompts,
      0
    );
    const majorityAlignedPrompts = rows.reduce(
      (sum, row) => sum + row.majorityAlignedPrompts,
      0
    );
    const splitSignalPrompts = rows.reduce(
      (sum, row) => sum + row.splitSignalPrompts,
      0
    );

    const lowest = rows[0];
    const highest = [...rows].sort((a, b) => {
      if (b.majorityAlignmentRate !== a.majorityAlignmentRate) {
        return b.majorityAlignmentRate - a.majorityAlignmentRate;
      }

      return b.fullConsensusRate - a.fullConsensusRate;
    })[0];

    return {
      comparablePrompts,
      fullConsensusPrompts,
      majorityAlignedPrompts,
      splitSignalPrompts,
      fullConsensusRate:
        comparablePrompts > 0 ? fullConsensusPrompts / comparablePrompts : 0,
      majorityAlignmentRate:
        comparablePrompts > 0 ? majorityAlignedPrompts / comparablePrompts : 0,
      lowest,
      highest
    };
  }, [rows]);

  const promptMix = useMemo(() => {
    const clear = consensusPrompts.filter((prompt) => prompt.status === "clear");
    const unclear = consensusPrompts.filter(
      (prompt) => prompt.status === "unclear"
    );
    const filtered =
      promptFilter === "all"
        ? consensusPrompts
        : consensusPrompts.filter((prompt) => prompt.status === promptFilter);

    return {
      total: consensusPrompts.length,
      clear: clear.length,
      unclear: unclear.length,
      filtered
    };
  }, [consensusPrompts, promptFilter]);

  function togglePromptMix(filter: PromptFilter) {
    if (showPromptMix && promptFilter === filter) {
      setShowPromptMix(false);
      setExpandedPromptId(null);
      return;
    }

    setPromptFilter(filter);
    setShowPromptMix(true);
    setExpandedPromptId(null);
  }

  const expectedScope = account && runId ? `${account}:${runId}` : null;
  if (status !== "ready" || !summary || loadedScope !== expectedScope) {
    return (
      <ModuleContainer
        title="AI Consensus"
        state={status === "ready" ? "loading" : status}
        message={status === "ready" ? "Loading consensus for the selected run…" : statusMessage}
      />
    );
  }

  return (
    <ModuleContainer title="AI Consensus">
      <p
        style={{
          marginBottom: 14,
          color: colors.textSecondary,
          fontSize: 14,
          lineHeight: 1.45
        }}
      >
        {getSubtitle(mode)}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 14
        }}
      >
        <Metric
          label="Overall Agreement"
          value={formatPercent(summary.majorityAlignmentRate)}
          sublabel={`Most models agreed on ${summary.majorityAlignedPrompts} of ${summary.comparablePrompts} prompts`}
        />

        <Metric
          label="Exact Agreement"
          value={formatPercent(summary.fullConsensusRate)}
          sublabel={`All models agreed on ${summary.fullConsensusPrompts} of ${summary.comparablePrompts} prompts`}
        />

        <Metric
          label="Prompt Coverage"
          value={String(summary.comparablePrompts)}
          sublabel={`Across ${modelLabels.length} monitored models`}
        />

        <Metric
          label="Unclear Signals"
          value={String(summary.splitSignalPrompts)}
          sublabel="Review prompts with no clear model majority"
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 14
        }}
      >
        <span
          style={{
            color: "#64748b",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase"
          }}
        >
          AI Platforms:
        </span>

        {modelLabels.map((model) => (
          <span
            key={model}
            style={{
              padding: "5px 9px",
              borderRadius: 999,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              color: colors.textSecondary,
              fontSize: 12,
              fontWeight: 700
            }}
          >
            {model}
          </span>
        ))}
      </div>

      <div
        style={{
          borderRadius: 14,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          padding: 13,
          marginBottom: 12
        }}
      >
        <p
          style={{
            margin: 0,
            color: colors.textSecondary,
            fontSize: 13,
            lineHeight: 1.5
          }}
        >
          {getNarrative({
            mode,
            highest: summary.highest,
            lowest: summary.lowest,
            majorityAlignmentRate: summary.majorityAlignmentRate,
            fullConsensusRate: summary.fullConsensusRate,
            comparablePrompts: summary.comparablePrompts
          })}
        </p>
      </div>

      {promptMix.total > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 14
          }}
        >
          {promptMix.unclear > 0 && (
            <button
              type="button"
              onClick={() => togglePromptMix("unclear")}
              className="toren-control toren-control--primary"
            >
              {showPromptMix && promptFilter === "unclear"
                ? "Hide unclear prompts"
                : `Review unclear prompts (${promptMix.unclear})`}
            </button>
          )}

          <button
            type="button"
            onClick={() => togglePromptMix("all")}
            className="toren-control toren-control--secondary"
          >
            {showPromptMix && promptFilter === "all"
              ? "Hide full prompt mix"
              : `View full prompt mix (${promptMix.total})`}
          </button>
        </div>
      )}

      {showPromptMix && promptMix.total > 0 && (
        <PromptMixPanel
          prompts={promptMix.filtered}
          mode={mode}
          activeFilter={promptFilter}
          counts={{
            all: promptMix.total,
            clear: promptMix.clear,
            unclear: promptMix.unclear
          }}
          expandedPromptId={expandedPromptId}
          onFilterChange={(filter) => {
            setPromptFilter(filter);
            setExpandedPromptId(null);
          }}
          onTogglePrompt={(promptId) =>
            setExpandedPromptId((current) =>
              current === promptId ? null : promptId
            )
          }
          onClose={() => {
            setShowPromptMix(false);
            setExpandedPromptId(null);
          }}
        />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 8
        }}
      >
        <CategoryRow
          label="Most Consistent Category"
          category={summary.highest.category}
          mode={mode}
          majorityRate={summary.highest.majorityAlignmentRate}
          fullRate={summary.highest.fullConsensusRate}
        />

        <CategoryRow
          label="Most Variable Category"
          category={summary.lowest.category}
          mode={mode}
          majorityRate={summary.lowest.majorityAlignmentRate}
          fullRate={summary.lowest.fullConsensusRate}
        />
      </div>
    </ModuleContainer>
  );
}

function Metric({
  label,
  value,
  sublabel
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0"
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: 10
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: colors.textPrimary,
          fontSize: 32,
          fontWeight: 900,
          lineHeight: 1
        }}
      >
        {value}
      </div>

      {sublabel && (
        <div
          style={{
            color: colors.textSecondary,
            fontSize: 12,
            fontWeight: 700,
            marginTop: 8,
            lineHeight: 1.25
          }}
        >
          {sublabel}
        </div>
      )}
    </div>
  );
}

function PromptMixPanel({
  prompts,
  mode,
  activeFilter,
  counts,
  expandedPromptId,
  onFilterChange,
  onTogglePrompt,
  onClose
}: {
  prompts: ConsensusPrompt[];
  mode?: Props["mode"];
  activeFilter: PromptFilter;
  counts: Record<PromptFilter, number>;
  expandedPromptId: string | null;
  onFilterChange: (filter: PromptFilter) => void;
  onTogglePrompt: (promptId: string) => void;
  onClose: () => void;
}) {
  const tabs: Array<{ key: PromptFilter; label: string }> = [
    { key: "all", label: "All prompts" },
    { key: "clear", label: "Clear agreement" },
    { key: "unclear", label: "Unclear agreement" }
  ];

  return (
    <div
      style={{
        border: "1px solid #dbe3ee",
        background: "#f8fafc",
        borderRadius: 16,
        padding: 14,
        marginBottom: 14
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12
        }}
      >
        <div>
          <div
            style={{
              color: colors.textPrimary,
              fontSize: 15,
              fontWeight: 900,
              marginBottom: 3
            }}
          >
            AI Consensus prompt mix
          </div>
          <div
            style={{
              color: colors.textSecondary,
              fontSize: 12,
              lineHeight: 1.4
            }}
          >
            Inspect the full prompt set and expand any row for model-level signals.
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="toren-control toren-control--tertiary"
        >
          Close
        </button>
      </div>

      <div
        role="tablist"
        aria-label="Prompt agreement filters"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 12
        }}
      >
        {tabs.map((tab) => {
          const active = tab.key === activeFilter;

          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onFilterChange(tab.key)}
              className="toren-tab"
            >
              {tab.label} ({counts[tab.key]})
            </button>
          );
        })}
      </div>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#ffffff"
        }}
      >
        <div style={{ minWidth: 780 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "minmax(330px, 2.2fr) minmax(130px, 0.8fr) 100px 125px 110px",
              gap: 12,
              padding: "9px 12px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              color: "#64748b",
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.05em",
              textTransform: "uppercase"
            }}
          >
            <span>Prompt</span>
            <span>Category</span>
            <span>Agreement</span>
            <span>Models agreeing</span>
            <span>Status</span>
          </div>

          {prompts.length === 0 ? (
            <div
              style={{
                padding: 18,
                color: colors.textSecondary,
                fontSize: 13,
                textAlign: "center"
              }}
            >
              No prompts match this filter.
            </div>
          ) : (
            prompts.map((prompt) => {
              const expanded = prompt.id === expandedPromptId;
              const clear = prompt.status === "clear";

              return (
                <div
                  key={prompt.id}
                  style={{ borderBottom: "1px solid #eef2f7" }}
                >
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => onTogglePrompt(prompt.id)}
                    style={{
                      width: "100%",
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(330px, 2.2fr) minmax(130px, 0.8fr) 100px 125px 110px",
                      gap: 12,
                      alignItems: "center",
                      padding: "11px 12px",
                      border: 0,
                      background: expanded ? "#f8fafc" : "#ffffff",
                      color: colors.textPrimary,
                      textAlign: "left",
                      cursor: "pointer"
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        lineHeight: 1.4
                      }}
                    >
                      {expanded ? "▾" : "›"} {prompt.promptText}
                    </span>
                    <span
                      style={{
                        color: colors.textSecondary,
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      {formatCategory(prompt.category, mode)}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 900 }}>
                      {formatPercent(prompt.agreementRate)}
                    </span>
                    <span
                      style={{
                        color: colors.textSecondary,
                        fontSize: 12,
                        fontWeight: 800
                      }}
                    >
                      {prompt.agreeingModels} of {prompt.modelCount}
                    </span>
                    <span
                      style={{
                        justifySelf: "start",
                        padding: "4px 8px",
                        borderRadius: 999,
                        border: clear
                          ? "1px solid #bbf7d0"
                          : "1px solid #fde68a",
                        background: clear ? "#f0fdf4" : "#fffbeb",
                        color: clear ? "#15803d" : "#b45309",
                        fontSize: 11,
                        fontWeight: 900,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {clear ? "Clear" : "Needs review"}
                    </span>
                  </button>

                  {expanded && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        padding: "0 12px 12px 32px",
                        background: "#f8fafc"
                      }}
                    >
                      {prompt.signals.map((signal) => (
                        <span
                          key={`${prompt.id}-${signal.provider}-${signal.signal}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "5px 9px",
                            borderRadius: 999,
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            color: colors.textSecondary,
                            fontSize: 12,
                            fontWeight: 800
                          }}
                        >
                          {signal.label}: {formatCategory(signal.signal)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  label,
  category,
  mode,
  majorityRate,
  fullRate
}: {
  label: string;
  category: string;
  mode?: Props["mode"];
  majorityRate: number;
  fullRate: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        padding: "9px 11px",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        background: "#ffffff"
      }}
    >
      <div>
        <div
          style={{
            color: "#64748b",
            fontSize: 11,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 3
          }}
        >
          {label}
        </div>

        <div
          style={{
            color: colors.textPrimary,
            fontSize: 13,
            fontWeight: 800
          }}
        >
          {formatCategory(category, mode)}
        </div>
      </div>

      <div
        style={{
          color: colors.textSecondary,
          fontSize: 12,
          fontWeight: 700,
          textAlign: "right",
          whiteSpace: "nowrap"
        }}
      >
        {formatPercent(majorityRate)} majority agreement
        <br />
        {formatPercent(fullRate)} exact agreement
      </div>
    </div>
  );
}
